import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import moment from "moment-timezone";
import { faEye, faInfo } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ErrorTooltip from "./ErrorTooltip";
import WaiveTooltip from "./WaiveTooltip";
import { useCompanyDateFormat } from "../../../../../../utils/hooks/company";
import { getSharedDateRowSpan, isNonProductiveTimeSheet } from "../../helpers";

export const useDateColumn = ({ dateTimesheetMap, view, viewSummary, waive, cancelWaive, isMyTimesheet }) => {
    const { t } = useTranslation();
    const dateFormat = useCompanyDateFormat();

    return useMemo(() => ({
        Header: t("date"),
        accessor: "date",
        headCustomProps: { className: "input-column" },
        getCellCustomProps: (timeSheet) => ({
            rowSpan: getSharedDateRowSpan(dateTimesheetMap, timeSheet),
        }),
        Cell: ({ value, row, errors, data }) => {
            const { isAdded } = row.original;
            const date = moment(row.original.date)
                .startOf("day")
                .toISOString(true);
            const timesheets = data?.filter((item) => moment(item.date).format("YYYY-MM-DD") === moment(date).format("YYYY-MM-DD"));
            const nonProductiveTimeSheets = timesheets.filter((timesheet) => isNonProductiveTimeSheet(timesheet));
            const manuallyUpdated = dateTimesheetMap?.[moment(date).format("YYYY-MM-DD")]?.find(
                ({ manuallyUpdated }) => manuallyUpdated);

            return (
                <div className="d-flex gap-x-2 align-items-center date-column">
                    {(
                        !(isNonProductiveTimeSheet(row.original) && timesheets.length > 1) ||
                        (isNonProductiveTimeSheet(row.original) && (nonProductiveTimeSheets.length === timesheets.length))
                    ) ? (
                        <>
                            <span>{moment(value, "YYYY-MM-DD").format(dateFormat)}</span>
                            <span style={{ width: '32px' }}>{moment(value, "YYYY-MM-DD").format(`ddd`).toUpperCase()}</span>
                        </>
                    ) : <div style={{ width: "107px" }} />}

                    {(
                        (view && !isNonProductiveTimeSheet(row.original)) ||
                        (view && isNonProductiveTimeSheet(row.original) && nonProductiveTimeSheets.length === timesheets.length)
                    ) && (
                            <span
                                className="cursor-pointer"
                                onClick={() => view(row.original)}
                            >
                                <FontAwesomeIcon
                                    icon={faInfo}
                                    size="lg"
                                    className={classNames(manuallyUpdated && "text-red")}
                                />
                            </span>
                        )}

                    {(
                        !isAdded &&
                        (row.original.appliedRules || []).includes("mealPremium")
                    ) && (
                            <WaiveTooltip
                                data={row.original}
                                waive={waive}
                                cancelWaive={cancelWaive}
                                isMyTimesheet={isMyTimesheet}
                                timesheets={timesheets}
                            />
                        )}

                    {(
                        (!isAdded && viewSummary && !isNonProductiveTimeSheet(row.original)) ||
                        (!isAdded && viewSummary && isNonProductiveTimeSheet(row.original) && nonProductiveTimeSheets.length === timesheets.length)
                    ) && (
                            <span
                                className="cursor-pointer"
                                onClick={() => viewSummary(date)}
                            >
                                <FontAwesomeIcon icon={faEye} size="lg" />
                            </span>
                        )}

                    <ErrorTooltip row={row} errors={errors} />
                </div>
            );
        },
        alwaysVisible: true,
    }), [t, dateTimesheetMap, dateFormat, view, viewSummary, waive, cancelWaive, isMyTimesheet]);
};
